//Assessment components
import Question from "./Question"

//DND-kit
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"

//PrimeReact
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup"

//Styles
import myStyles from "../../../../../styles/assessments/ManagementFormBuilder.module.css"
import assessStyles from "../../../../../styles/assessments/Assessment.module.css"
import { Panel } from "primereact/panel"
import { useDroppable } from "@dnd-kit/core"
import { useEffect, useRef } from "react"
import QuestionOrderButtons from "./QuestionOrderButtons"
import { CSS } from "@dnd-kit/utilities"
import QuestionOrderGroupQuestion from "./QuestionOrderGroupQuestion"
import {
  validateMultiValueType,
  validateSingeValueType,
} from "../../../AssessmentForm/helpers/conditionalQuestionValidator"
import elementTypes from "../Elements/elementTypes"
import { useFormikContext } from "formik"

export default function QuestionOrder({
  previewMode,
  disabled,
  questionOrder,
  selectedQuestionId,
  answers,
  onSetAnswer,
  isSelected,
  onDeleteOrder,
  onEditOrder,
  onDeleteQuestion,
  onEditQuestion,
}) {
  //Prep a droppable ref to render a Group id required
  const { setNodeRef: setDroppableNodeRef } = useDroppable({
    id: `questionGroup_${questionOrder.questionGroup?.id}`,
    data: { questionGroup: questionOrder.questionGroup },
  })

  const { errors, values } = useFormikContext()
  const questionOrderIndex = values.questions?.findIndex(
    (question) => question.id === questionOrder.id
  )
  let hasError = false

  if (errors?.questions && errors?.questions[questionOrderIndex]) {
    if (questionOrder?.questionGroup) {
      const questionGroupErrors =
        errors?.questions?.[questionOrderIndex]?.questionGroup

      if (
        questionGroupErrors &&
        Object.keys(questionGroupErrors).filter((key) => key !== "questions")
          .length > 0
      ) {
        const questionGroupInError = !isSelected || !!selectedQuestionId

        hasError = questionGroupInError
      }
    } else {
      hasError = !isSelected
    }
  }

  const questionOrderRef = useRef()
  const questionGroupPanelRef = useRef()

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: questionOrder.id,
    data: { questionOrder: questionOrder },
  })

  let width = "100%"

  if (questionOrder.question && previewMode) {
    width = questionOrder.question.width || "100%"
  }

  useEffect(() => {
    if (isSelected && questionOrderRef.current && !selectedQuestionId) {
      const rect = questionOrderRef.current.getBoundingClientRect()

      //Check if the question is in the viewport
      const isVisible =
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <=
          (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <=
          (window.innerWidth || document.documentElement.clientWidth)

      if (!isVisible) {
        questionOrderRef.current.scrollIntoView()
      }
    }
  }, [isSelected, questionOrderRef, selectedQuestionId])

  useEffect(() => {
    if (questionGroupPanelRef.current) {
      if (previewMode) {
        if (questionOrder.questionGroup.expanded === false) {
          questionGroupPanelRef.current.collapse()
        }
      } else {
        questionGroupPanelRef.current.expand()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionGroupPanelRef, previewMode])

  const style = {
    transition,
    transform: CSS.Transform.toString(transform),
  }

  let child = <>Unknown element</>
  let questionConditionalResult = undefined
  let showQuestion = true

  let questionGroupQuestions = []
  let useQuestionLayout = true

  if (questionOrder.question) {
    const question = questionOrder.question
    if (question.conditionalSource && previewMode) {
      const sourceAnswer = answers.find(
        (answer) => answer.questionId === question.conditionalSource
      )

      if (sourceAnswer) {
        if (sourceAnswer.questionType === elementTypes.checkboxElementId) {
          questionConditionalResult = validateMultiValueType(
            question,
            sourceAnswer.value
          )
        } else {
          questionConditionalResult = validateSingeValueType(
            question,
            sourceAnswer.value
          )
        }
      }

      if (questionConditionalResult) {
        showQuestion = questionConditionalResult.display
      } else if (question.conditionalDisplay) {
        showQuestion = false
      }
    }

    child = (
      <Question
        previewMode={previewMode}
        disabled={disabled}
        question={questionOrder.question}
        answers={answers}
        conditionalConfig={questionConditionalResult}
        onSetAnswer={onSetAnswer}
        isSelected={selectedQuestionId === questionOrder.question.id}
      />
    )
  } else if (questionOrder.questionGroup) {
    useQuestionLayout = false
    questionGroupQuestions = JSON.parse(
      JSON.stringify(
        questionOrder.questionGroup.questions.filter(
          (question) => question.isActive
        )
      )
    )
  }

  const confirmDelete = (event, questionId) => {
    confirmPopup({
      target: event.currentTarget,
      message: "Are you sure you want to delete this element?",
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "accept",
      accept: () => {
        if (questionId) {
          onDeleteQuestion(questionId)
        } else {
          onDeleteOrder()
        }
      },
    })
  }

  if (!showQuestion) {
    return null
  }

  return (
    <div ref={setNodeRef} style={style}>
      <ConfirmPopup />
      {useQuestionLayout ? (
        <div
          className={`${myStyles.QuestionOrderContainer} ${
            disabled ? myStyles.QuestionOrderContainerDisabled : ""
          } ${
            (isDragging || isSelected) && !previewMode
              ? myStyles.selectedBorder
              : ""
          } ${previewMode ? myStyles.QuestionOrderContainerPreview : ""} ${
            hasError ? myStyles.questionOrderError : ""
          }`}
          ref={questionOrderRef}
          style={{ width: width }}
        >
          <div className={myStyles.QuestionOrderContainerQuestion}>{child}</div>
          {!previewMode ? (
            <div className={myStyles.QuestionOrderContainerControls}>
              <QuestionOrderButtons
                onEdit={onEditOrder}
                onDelete={(event) => confirmDelete(event)}
                dndAttributes={attributes}
                dndListeners={listeners}
                disabled={disabled}
              />
            </div>
          ) : null}
        </div>
      ) : (
        <div
          className={`${myStyles.questionGroupContainer} ${
            (isDragging || isSelected) && !previewMode && !selectedQuestionId
              ? myStyles.selectedGroupBorder
              : ""
          } ${hasError ? myStyles.questionOrderError : ""}`}
          ref={questionOrderRef}
        >
          <Panel
            header={
              <div className={`${myStyles.questionGroupLayout} `}>
                <div
                  className={`${myStyles.questionGroupLayoutName} ${
                    myStyles.questionGroupLayoutNameTitle
                  } ${
                    !questionOrder.questionGroup.name
                      ? myStyles.placeHolder
                      : ""
                  }`}
                >
                  {questionOrder.questionGroup.name || previewMode
                    ? questionOrder.questionGroup.name
                    : "No name specified"}
                </div>
                {!previewMode ? (
                  <div className={myStyles.questionGroupLayoutControls}>
                    <QuestionOrderButtons
                      onEdit={onEditOrder}
                      onDelete={(event) => confirmDelete(event)}
                      dndAttributes={attributes}
                      dndListeners={listeners}
                      disabled={disabled}
                    />
                  </div>
                ) : null}
              </div>
            }
            toggleable
            className={`${assessStyles.groupContainer} `}
            ref={questionGroupPanelRef}
          >
            <div
              className={`${
                questionOrder.questionGroup.alignment === "horizontal"
                  ? assessStyles.groupContainerHorizontal
                  : ""
              } ${myStyles.questionGroupDroppable} `}
              ref={setDroppableNodeRef}
            >
              <SortableContext
                items={questionGroupQuestions.map((question) => question.id)}
                strategy={verticalListSortingStrategy}
                disabled={disabled}
              >
                {questionGroupQuestions.map((question) => {
                  return (
                    <>
                      <QuestionOrderGroupQuestion
                        previewMode={previewMode}
                        disabled={disabled}
                        questionGroup={questionOrder.questionGroup}
                        question={question}
                        selectedQuestionId={selectedQuestionId}
                        answers={answers}
                        onSetAnswer={(answer) => {
                          onSetAnswer(answer, question)
                        }}
                        onEdit={() => {
                          onEditQuestion(question.id)
                        }}
                        onDelete={(event) => {
                          confirmDelete(event, question.id)
                        }}
                      />
                    </>
                  )
                })}
                {!previewMode ? (
                  <div className={myStyles.questionGroupDroppablePaddingDiv}>
                    {questionGroupQuestions.length === 0
                      ? ">>Drop new questions here<<"
                      : ""}
                  </div>
                ) : null}
              </SortableContext>
            </div>
          </Panel>
        </div>
      )}
    </div>
  )
}
